/**
 * Zod schemas for every API response the console parses.
 *
 * Two rules keep this file honest:
 *  - shapes that already exist in `apps/api/src/**\/*.dto.ts` are mirrored field
 *    for field;
 *  - shapes whose module is still a 501 stub follow the documented contract
 *    (docs/02 section 4, docs/06 section 4) and are listed in apps/web/NEEDS.md.
 *
 * Every object is `.loose()`: the API may grow fields before the console knows
 * about them, and a new field must never blank a page.
 */
import { z } from 'zod';
import { ALARM_STATES, CONDITION_OPERATORS, RULE_SETTABLE_STATES, SEVERITIES } from '@vam/schema';

export const RoleSchema = z.enum(['owner', 'admin', 'operator', 'viewer']);
export type Role = z.infer<typeof RoleSchema>;

export const SeveritySchema = z.enum(SEVERITIES);
export type Severity = z.infer<typeof SeveritySchema>;

export { ALARM_STATES };
export const AlarmStateSchema = z.enum(ALARM_STATES);
export type AlarmState = z.infer<typeof AlarmStateSchema>;

const isoish = z.string();
const jsonRecord = z.record(z.string(), z.unknown());

export function pageOf<T extends z.ZodTypeAny>(item: T) {
  return z
    .object({ items: z.array(item), next_cursor: z.string().nullable().default(null) })
    .loose();
}

// --- Identity ---------------------------------------------------------------

export const UserRefSchema = z
  .object({
    id: z.string(),
    display_name: z.string().default(''),
    email: z.string().default(''),
  })
  .loose();
export type UserRef = z.infer<typeof UserRefSchema>;

export const TeamRefSchema = z
  .object({ id: z.string(), name: z.string().default(''), slug: z.string().default('') })
  .loose();
export type TeamRef = z.infer<typeof TeamRefSchema>;

export const ChannelPrefsSchema = z
  .object({
    email: z.boolean().optional(),
    sms: z.boolean().optional(),
    voice: z.boolean().optional(),
    teams: z.boolean().optional(),
    web_push: z.boolean().optional(),
  })
  .loose();
export type ChannelPrefs = z.infer<typeof ChannelPrefsSchema>;

export const QuietHoursSchema = z
  .object({
    start: z.string(),
    end: z.string(),
    days: z.array(z.number().int()).optional(),
  })
  .loose();
export type QuietHours = z.infer<typeof QuietHoursSchema>;

export const UserSchema = z
  .object({
    id: z.string(),
    email: z.string(),
    display_name: z.string(),
    role: RoleSchema,
    is_active: z.boolean().default(true),
    entra_linked: z.boolean().default(false),
    has_password: z.boolean().default(false),
    phone: z.string().nullable().default(null),
    timezone: z.string().default('UTC'),
    channel_prefs: ChannelPrefsSchema.prefault({}),
    quiet_hours: QuietHoursSchema.nullable().default(null),
    teams: z.array(TeamRefSchema).default([]),
    last_login_at: isoish.nullable().default(null),
    created_at: isoish,
    updated_at: isoish,
  })
  .loose();
export type User = z.infer<typeof UserSchema>;

export const MeSchema = z
  .object({
    kind: z.enum(['user', 'api_key']),
    id: z.string().nullable(),
    label: z.string(),
    role: RoleSchema,
    team_ids: z.array(z.string()).default([]),
    auth_method: z.enum(['session', 'api_key', 'dev']).nullable().default(null),
    session_expires_at: isoish.nullable().default(null),
    user: UserSchema.nullable().default(null),
  })
  .loose();
export type Me = z.infer<typeof MeSchema>;

export const TeamSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    member_count: z.number().int().default(0),
    deleted_at: isoish.nullable().default(null),
    created_at: isoish,
    updated_at: isoish,
  })
  .loose();
export type Team = z.infer<typeof TeamSchema>;

export const ContactSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    email: z.string().nullable().default(null),
    phone: z.string().nullable().default(null),
    team_id: z.string().nullable().default(null),
    channels: z.array(z.string()).default([]),
    created_at: isoish,
    updated_at: isoish,
  })
  .loose();
export type Contact = z.infer<typeof ContactSchema>;

export const ApiKeySchema = z
  .object({
    id: z.string(),
    name: z.string(),
    prefix: z.string(),
    role: RoleSchema,
    team_id: z.string().nullable().default(null),
    created_by_id: z.string().optional(),
    last_used_at: isoish.nullable().default(null),
    expires_at: isoish.nullable().default(null),
    revoked_at: isoish.nullable().default(null),
    created_at: isoish,
    updated_at: isoish,
  })
  .loose();
export type ApiKey = z.infer<typeof ApiKeySchema>;

export const CreatedApiKeySchema = ApiKeySchema.extend({ token: z.string() });
export type CreatedApiKey = z.infer<typeof CreatedApiKeySchema>;

// --- Alarms -----------------------------------------------------------------

export const AlarmEntitySchema = z
  .object({
    name: z.string().default(''),
    kind: z.string().default('other'),
    external_id: z.string().nullable().default(null),
    url: z.string().nullable().default(null),
  })
  .loose();

export const IntegrationRefSchema = z
  .object({
    id: z.string(),
    name: z.string().default(''),
    source_type: z.string().default('webhook'),
  })
  .loose();

export const TicketLinkSchema = z
  .object({
    id: z.string(),
    system: z.string(),
    external_id: z.string(),
    external_url: z.string().nullable().default(null),
    status: z.string().nullable().default(null),
    outbound_integration_id: z.string().nullable().default(null),
    last_synced_at: isoish.nullable().default(null),
    created_at: isoish.optional(),
  })
  .loose();
export type TicketLink = z.infer<typeof TicketLinkSchema>;

export const NotificationSummarySchema = z
  .object({
    total: z.number().int().default(0),
    by_status: z.record(z.string(), z.number().int()).default({}),
    by_channel: z.record(z.string(), z.number().int()).default({}),
    last_sent_at: isoish.nullable().default(null),
  })
  .loose();
export type NotificationSummary = z.infer<typeof NotificationSummarySchema>;

/**
 * The list row (`apps/api/src/alarms/alarms.dto.ts` `AlarmSchema`). References
 * are ids; the console resolves names from the integration, team and user
 * lookups it already holds. `GET /alarms/:id` adds the expanded objects, so
 * they are optional here and present on the detail page.
 */
export const AlarmSchema = z
  .object({
    id: z.string(),
    integration_id: z.string(),
    team_id: z.string(),
    dedup_key: z.string().default(''),
    state: AlarmStateSchema,
    severity: SeveritySchema,
    title: z.string(),
    message: z.string().default(''),
    entity: AlarmEntitySchema.prefault({}),
    category: z.string().nullable().default(null),
    first_seen: isoish,
    last_seen: isoish,
    occurrence_count: z.number().int().default(1),
    acknowledged_by_id: z.string().nullable().default(null),
    acknowledged_at: isoish.nullable().default(null),
    assignee_id: z.string().nullable().default(null),
    closed_by_id: z.string().nullable().default(null),
    closed_by_system: z.boolean().default(false),
    closed_at: isoish.nullable().default(null),
    close_reason: z.string().nullable().default(null),
    snoozed_until: isoish.nullable().default(null),
    snoozed_from: AlarmStateSchema.nullable().default(null),
    suppressed: z.boolean().default(false),
    suppressed_by_id: z.string().nullable().default(null),
    flapping: z.boolean().default(false),
    escalation_version: z.number().int().default(0),
    fields: jsonRecord.default({}),
    tags: z.array(z.string()).default([]),
    deleted_at: isoish.nullable().default(null),
    created_at: isoish,
    updated_at: isoish,
    // Detail-only expansions.
    integration: IntegrationRefSchema.nullable().optional(),
    assignee: UserRefSchema.nullable().optional(),
    acknowledged_by: UserRefSchema.nullable().optional(),
    closed_by: UserRefSchema.nullable().optional(),
    tickets: z.array(TicketLinkSchema).optional(),
    notifications: NotificationSummarySchema.optional(),
    comment_count: z.number().int().optional(),
  })
  .loose();
export type Alarm = z.infer<typeof AlarmSchema>;

/** `GET /alarms?group_by=…` answers with groups only (no cursor). */
export const AlarmGroupSchema = z
  .object({
    key: z.string().nullable().default(null),
    label: z.string().nullable().default(null),
    count: z.number().int().default(0),
    latest_alarm: AlarmSchema.nullable().default(null),
    top_alarms: z.array(AlarmSchema).default([]),
  })
  .loose();
export type AlarmGroup = z.infer<typeof AlarmGroupSchema>;

export const AlarmPageSchema = z
  .object({
    items: z.array(AlarmSchema).default([]),
    next_cursor: z.string().nullable().default(null),
    /** Present instead of `items` when `group_by` is sent. */
    groups: z.array(AlarmGroupSchema).optional(),
  })
  .loose();
export type AlarmPage = z.infer<typeof AlarmPageSchema>;

export const TIMELINE_KINDS = ['activity', 'comment', 'notification'] as const;

export const TimelineActorSchema = z
  .object({
    user_id: z.string().nullable().default(null),
    label: z.string().nullable().default(null),
    system: z.string().nullable().default(null),
  })
  .loose();
export type TimelineActor = z.infer<typeof TimelineActorSchema>;

/**
 * One timeline row. The kind-specific detail lives in `payload`; the helpers in
 * `src/features/alarms/timeline.ts` read it, so no component pokes at raw JSON.
 */
export const TimelineItemSchema = z
  .object({
    id: z.string(),
    kind: z.enum(TIMELINE_KINDS),
    type: z.string().default(''),
    created_at: isoish,
    actor: TimelineActorSchema.nullable().default(null),
    payload: jsonRecord.default({}),
  })
  .loose();
export type TimelineItem = z.infer<typeof TimelineItemSchema>;

/** A raw event as the "Events" tab shows it (`GET /alarms/:id/events`). */
export const AlarmEventSchema = z
  .object({
    id: z.string(),
    integration_id: z.string(),
    source_event_id: z.string().default(''),
    dedup_key: z.string().default(''),
    sequence: z.string().nullable().default(null),
    event_type: z.string().default('update'),
    severity: SeveritySchema,
    title: z.string().default(''),
    message: z.string().default(''),
    entity: AlarmEntitySchema.prefault({}),
    category: z.string().nullable().default(null),
    occurred_at: isoish,
    received_at: isoish,
    fields: jsonRecord.default({}),
    tags: z.array(z.string()).default([]),
    stale: z.boolean().default(false),
    orphan: z.boolean().default(false),
    created_at: isoish.optional(),
  })
  .loose();
export type AlarmEvent = z.infer<typeof AlarmEventSchema>;

/** `POST /alarms/bulk/:action`: each id succeeds or fails on its own. */
export const BulkResultSchema = z
  .object({
    id: z.string(),
    ok: z.boolean(),
    state: z.string().optional(),
    status: z.number().int().optional(),
    detail: z.string().optional(),
  })
  .loose();
export const BulkResponseSchema = z
  .object({
    action: z.string(),
    succeeded: z.number().int(),
    failed: z.number().int(),
    results: z.array(BulkResultSchema).default([]),
  })
  .loose();
export type BulkResponse = z.infer<typeof BulkResponseSchema>;

export const SavedViewSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    team_id: z.string().nullable().default(null),
    user_id: z.string().nullable().default(null),
    filter: jsonRecord.default({}),
    sort: z.unknown().nullable().default(null),
    group_by: z.string().nullable().default(null),
    shared: z.boolean().default(false),
    is_default: z.boolean().default(false),
    position: z.number().int().default(0),
    created_at: isoish,
    updated_at: isoish,
  })
  .loose();
export type SavedView = z.infer<typeof SavedViewSchema>;

// --- Integrations -----------------------------------------------------------

export const IntegrationSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    source_type: z.string(),
    kind: z.enum(['push', 'poll']),
    team_id: z.string(),
    enabled: z.boolean().default(true),
    config: jsonRecord.default({}),
    secrets: z.record(z.string(), z.boolean()).default({}),
    has_api_key: z.boolean().default(false),
    has_url_token: z.boolean().default(false),
    url_token_enabled: z.boolean().default(false),
    ingest_url: z.string().nullable().default(null),
    poll_interval_seconds: z.number().int().nullable().default(null),
    fingerprint_fields: z.array(z.string()).nullable().default(null),
    last_poll_at: isoish.nullable().default(null),
    last_error: z.string().nullable().default(null),
    consecutive_failures: z.number().int().default(0),
    created_at: isoish,
    updated_at: isoish,
  })
  .loose();
export type Integration = z.infer<typeof IntegrationSchema>;

export const SetupSnippetSchema = z
  .object({
    title: z.string(),
    language: z.string().default('text'),
    filename: z.string().optional(),
    content: z.string(),
  })
  .loose();
export type SetupSnippet = z.infer<typeof SetupSnippetSchema>;

export const SetupInstructionsSchema = z
  .object({
    source_type: z.string(),
    title: z.string(),
    summary: z.string().default(''),
    steps: z.array(z.string()).default([]),
    snippets: z.array(SetupSnippetSchema).default([]),
    links: z.array(z.object({ label: z.string(), url: z.string() }).loose()).default([]),
    ingest_url: z.string().nullable().default(null),
  })
  .loose();
export type SetupInstructions = z.infer<typeof SetupInstructionsSchema>;

export const IntegrationTestResultSchema = z
  .object({
    ok: z.boolean(),
    kind: z.enum(['push', 'poll']),
    checked_at: isoish,
    url: z.string().optional(),
    curl: z.string().optional(),
    detail: z.string().optional(),
    status: z.number().int().optional(),
    error: z.string().optional(),
    duration_ms: z.number().int().optional(),
  })
  .loose();
export type IntegrationTestResult = z.infer<typeof IntegrationTestResultSchema>;

export const RotatedApiKeySchema = z
  .object({
    integration_id: z.string(),
    api_key: z.string(),
    prefix: z.string().default(''),
    header: z.string().default('Authorization: Bearer <api_key>'),
  })
  .loose();
export type RotatedApiKey = z.infer<typeof RotatedApiKeySchema>;

export const RotatedUrlTokenSchema = z
  .object({
    integration_id: z.string(),
    url_token: z.string(),
    url_token_enabled: z.boolean().default(true),
    ingest_url: z.string().nullable().default(null),
  })
  .loose();
export type RotatedUrlToken = z.infer<typeof RotatedUrlTokenSchema>;

export const OutboundSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    type: z.string(),
    team_id: z.string().nullable().default(null),
    enabled: z.boolean().default(true),
    config: jsonRecord.default({}),
    secrets: z.record(z.string(), z.boolean()).default({}),
    channels: z.array(z.string()).default([]),
    last_error: z.string().nullable().default(null),
    created_at: isoish,
    updated_at: isoish,
  })
  .loose();
export type Outbound = z.infer<typeof OutboundSchema>;

export const OutboundTestResultSchema = z
  .object({
    ok: z.boolean(),
    type: z.string(),
    checked_at: isoish,
    duration_ms: z.number().int().default(0),
    detail: z.string().optional(),
    status: z.number().int().optional(),
    provider_message_id: z.string().optional(),
    error: z.string().optional(),
  })
  .loose();
export type OutboundTestResult = z.infer<typeof OutboundTestResultSchema>;

// --- Rules, policies, escalations -------------------------------------------

export const ConditionOperatorSchema = z.enum(CONDITION_OPERATORS);

/** Mirrors `RULE_ACTION_TYPES` / `RuleActionSchema` in apps/api/src/rules/rules.dto.ts. */
export const RULE_ACTION_TYPES = [
  'set_severity',
  'set_state',
  'drop',
  'set_field',
  'remove_field',
  'add_tags',
  'remove_tags',
  'set_team',
  'set_assignee',
  'rename_field',
] as const;
export type RuleActionType = (typeof RULE_ACTION_TYPES)[number];

const FieldValueSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);

export const RuleActionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('set_severity'), severity: SeveritySchema }).loose(),
  z.object({ type: z.literal('set_state'), state: z.enum(RULE_SETTABLE_STATES) }).loose(),
  z.object({ type: z.literal('drop') }).loose(),
  z.object({ type: z.literal('set_field'), field: z.string(), value: FieldValueSchema }).loose(),
  z.object({ type: z.literal('remove_field'), field: z.string() }).loose(),
  z.object({ type: z.literal('add_tags'), tags: z.array(z.string()) }).loose(),
  z.object({ type: z.literal('remove_tags'), tags: z.array(z.string()) }).loose(),
  z.object({ type: z.literal('set_team'), team_id: z.string() }).loose(),
  z.object({ type: z.literal('set_assignee'), user_id: z.string().nullable() }).loose(),
  z.object({ type: z.literal('rename_field'), from: z.string(), to: z.string() }).loose(),
]);
export type RuleAction = z.infer<typeof RuleActionSchema>;

export const ProcessingRuleSchema = z
  .object({
    id: z.string(),
    team_id: z.string().nullable().default(null),
    name: z.string(),
    enabled: z.boolean().default(true),
    position: z.number().int().default(0),
    conditions: z.unknown().default({ all: [] }),
    actions: z.array(RuleActionSchema).default([]),
    continue_after: z.boolean().default(true),
    created_at: isoish,
    updated_at: isoish,
  })
  .loose();
export type ProcessingRule = z.infer<typeof ProcessingRuleSchema>;

/** One rule's verdict in a dry run (`POST /processing-rules/dry-run`). */
export const RuleDryRunMatchSchema = z
  .object({
    rule_id: z.string(),
    name: z.string().default(''),
    position: z.number().int().default(0),
    enabled: z.boolean().default(true),
    matched: z.boolean().default(false),
    /** False once an earlier match stopped the chain (`continue_after: false`). */
    evaluated: z.boolean().default(true),
    continue_after: z.boolean().default(true),
    actions: z.array(z.unknown()).default([]),
  })
  .loose();
export type RuleDryRunMatch = z.infer<typeof RuleDryRunMatchSchema>;

export const RuleDryRunSchema = z
  .object({
    matched: z.boolean().default(false),
    matched_rule_ids: z.array(z.string()).default([]),
    rules: z.array(RuleDryRunMatchSchema).default([]),
  })
  .loose();
export type RuleDryRun = z.infer<typeof RuleDryRunSchema>;

export const TARGET_TYPES = [
  'user',
  'contact',
  'team',
  'schedule',
  'escalation_policy',
  'outbound_integration',
] as const;
export type TargetType = (typeof TARGET_TYPES)[number];

/**
 * `apps/api/src/policies/targets.dto.ts` is a discriminated union whose
 * reference field is named after the type (`user_id`, `schedule_id`, …). Every
 * id is optional here so one shape parses them all; `targetRef`
 * (`src/features/policies/targets.ts`) reads the right one.
 */
export const NotificationTargetSchema = z
  .object({
    type: z.enum(TARGET_TYPES),
    user_id: z.string().optional(),
    contact_id: z.string().optional(),
    team_id: z.string().optional(),
    schedule_id: z.string().optional(),
    escalation_policy_id: z.string().optional(),
    outbound_integration_id: z.string().optional(),
    /** Schedule targets only: notify the current shift, the next one, or both. */
    shift: z.enum(['current', 'next', 'both']).optional(),
  })
  .loose();
export type NotificationTarget = z.infer<typeof NotificationTargetSchema>;

export const NOTIFY_CHANNELS = [
  'email',
  'teams',
  'slack',
  'sms',
  'voice',
  'web_push',
  'webhook',
  'pagerduty',
  'servicenow',
  'jira',
] as const;

export const NotificationDigestSchema = z
  .object({
    window_minutes: z.number().int().default(60),
    max_items: z.number().int().optional(),
  })
  .loose();
export type NotificationDigest = z.infer<typeof NotificationDigestSchema>;

/**
 * Storm guard: hold individual notifications during a flood and send one
 * summary. Null leaves the guard off, which is the default.
 */
export const NotificationStormGuardSchema = z
  .object({
    threshold: z.number().int().default(25),
    window_minutes: z.number().int().default(5),
    cooldown_minutes: z.number().int().default(15),
    max_minutes: z.number().int().default(120),
    max_items: z.number().int().default(50),
  })
  .loose();
export type NotificationStormGuard = z.infer<typeof NotificationStormGuardSchema>;

/** One storm the notification worker is holding right now. */
export const ActiveStormSchema = z
  .object({
    storm_id: z.string(),
    policy_id: z.string(),
    policy_name: z.string(),
    team_id: z.string().nullable().default(null),
    integration_id: z.string(),
    integration_name: z.string(),
    started_at: isoish,
    last_alarm_at: isoish,
    threshold: z.number().int().default(0),
    window_minutes: z.number().int().default(0),
    rate: z.number().int().default(0),
    held: z.number().int().default(0),
  })
  .loose();
export type ActiveStorm = z.infer<typeof ActiveStormSchema>;

export const ActiveStormListSchema = z.object({ items: z.array(ActiveStormSchema).default([]) });

export const NotificationPolicySchema = z
  .object({
    id: z.string(),
    /** Null is a global policy, evaluated for every team. */
    team_id: z.string().nullable().default(null),
    name: z.string(),
    enabled: z.boolean().default(true),
    conditions: z.unknown().default({ all: [] }),
    targets: z.array(NotificationTargetSchema).default([]),
    channels: z.array(z.string()).default([]),
    renotify_after_minutes: z.number().int().nullable().default(null),
    digest: NotificationDigestSchema.nullable().default(null),
    storm_guard: NotificationStormGuardSchema.nullable().default(null),
    escalation_policy_id: z.string().nullable().default(null),
    created_at: isoish,
    updated_at: isoish,
  })
  .loose();
export type NotificationPolicy = z.infer<typeof NotificationPolicySchema>;

export const EscalationLevelSchema = z
  .object({
    id: z.string().optional(),
    level: z.number().int(),
    timeout_minutes: z.number().int().default(15),
    targets: z.array(NotificationTargetSchema).default([]),
    channels: z.array(z.string()).default([]),
  })
  .loose();
export type EscalationLevel = z.infer<typeof EscalationLevelSchema>;

export const EscalationPolicySchema = z
  .object({
    id: z.string(),
    team_id: z.string(),
    name: z.string(),
    description: z.string().nullable().default(null),
    repeat_count: z.number().int().default(0),
    repeat_every_minutes: z.number().int().nullable().default(null),
    levels: z.array(EscalationLevelSchema).default([]),
    created_at: isoish,
    updated_at: isoish,
  })
  .loose();
export type EscalationPolicy = z.infer<typeof EscalationPolicySchema>;

// --- Schedules --------------------------------------------------------------

export const ROTATION_TYPES = ['daily', 'weekly', 'monthly', 'custom'] as const;

export const ShiftSchema = z
  .object({
    id: z.string(),
    schedule_id: z.string().optional(),
    name: z.string(),
    /** `HH:MM` in the schedule's timezone. */
    start_time: z.string().default('09:00'),
    end_time: z.string().default('17:00'),
    weekdays: z.array(z.number().int()).default([]),
    rotation_type: z.string().default('weekly'),
    rotation_length: z.number().int().default(1),
    rotation_start: isoish,
    participants: z.array(z.union([z.string(), UserRefSchema])).default([]),
    created_at: isoish.optional(),
    updated_at: isoish.optional(),
  })
  .loose();
export type Shift = z.infer<typeof ShiftSchema>;

export const ScheduleSchema = z
  .object({
    id: z.string(),
    team_id: z.string(),
    name: z.string(),
    timezone: z.string().default('UTC'),
    start_date: z.string(),
    end_date: z.string().nullable().default(null),
    excluded_weekdays: z.array(z.number().int()).default([]),
    shifts: z.array(ShiftSchema).default([]),
    created_at: isoish,
    updated_at: isoish,
  })
  .loose();
export type Schedule = z.infer<typeof ScheduleSchema>;

export const OVERRIDE_STATUSES = ['active', 'upcoming', 'completed'] as const;
export const OverrideStatusSchema = z.enum(OVERRIDE_STATUSES);
export type OverrideStatus = z.infer<typeof OverrideStatusSchema>;

export const OverrideSchema = z
  .object({
    id: z.string(),
    schedule_id: z.string(),
    shift_id: z.string().nullable().default(null),
    user_id: z.string(),
    replaces_user_id: z.string().nullable().default(null),
    starts_at: isoish,
    ends_at: isoish,
    note: z.string().nullable().default(null),
    status: OverrideStatusSchema.default('active'),
    created_at: isoish.optional(),
    updated_at: isoish.optional(),
  })
  .loose();
export type Override = z.infer<typeof OverrideSchema>;

export const OnCallUserSchema = z
  .object({
    id: z.string(),
    email: z.string().default(''),
    display_name: z.string().default(''),
    timezone: z.string().default('UTC'),
  })
  .loose();
export type OnCallUser = z.infer<typeof OnCallUserSchema>;

/** One block of cover: `GET /schedules/:id/calendar` returns a list of these. */
export const CalendarEntrySchema = z
  .object({
    schedule_id: z.string(),
    shift_id: z.string().nullable().default(null),
    shift_name: z.string().nullable().default(null),
    user_ids: z.array(z.string()).default([]),
    users: z.array(OnCallUserSchema).default([]),
    starts_at: isoish,
    ends_at: isoish,
    source: z.enum(['shift', 'override', 'none']).default('shift'),
  })
  .loose();
export type CalendarEntry = z.infer<typeof CalendarEntrySchema>;

export const OnCallResponseSchema = z
  .object({
    schedule_id: z.string(),
    at: isoish,
    source: z.enum(['shift', 'override', 'none']).default('none'),
    shift_id: z.string().nullable().default(null),
    shift_name: z.string().nullable().default(null),
    user_ids: z.array(z.string()).default([]),
    users: z.array(OnCallUserSchema).default([]),
    starts_at: isoish.nullable().default(null),
    ends_at: isoish.nullable().default(null),
    next: CalendarEntrySchema.nullable().default(null),
  })
  .loose();
export type OnCallResponse = z.infer<typeof OnCallResponseSchema>;

export const CalendarResponseSchema = z
  .object({
    schedule_id: z.string(),
    from: isoish,
    to: isoish,
    timezone: z.string().default('UTC'),
    entries: z.array(CalendarEntrySchema).default([]),
  })
  .loose();
export type CalendarResponse = z.infer<typeof CalendarResponseSchema>;

// --- Maintenance ------------------------------------------------------------

/**
 * A window's scope, as stored. The API keeps the object it was given, and both
 * singular and plural spellings are in the wild, so every list is optional and
 * `maintenanceScopeChips` (src/features/maintenance) is what renders it.
 */
/**
 * Mirrors `MaintenanceScopeSchema` in packages/core/src/maintenance/scope.ts:
 * `global`, id lists, tags, and `entity_match`, a condition-DSL tree the
 * console builds from its entity-name and severity inputs. The singular spellings
 * are read for display only; the API never accepted them.
 */
export const MaintenanceScopeSchema = z
  .object({
    global: z.boolean().optional(),
    integration_ids: z.array(z.string()).optional(),
    team_ids: z.array(z.string()).optional(),
    tags: z.array(z.string()).optional(),
    entity_match: z.unknown().optional(),
    integrations: z.array(z.string()).optional(),
    teams: z.array(z.string()).optional(),
  })
  .loose();
export type MaintenanceScope = z.infer<typeof MaintenanceScopeSchema>;

export const MaintenanceWindowSchema = z
  .object({
    id: z.string(),
    team_id: z.string().nullable().default(null),
    name: z.string(),
    starts_at: isoish,
    ends_at: isoish.nullable().default(null),
    rrule: z.string().nullable().default(null),
    duration_minutes: z.number().int().nullable().default(null),
    timezone: z.string().default('UTC'),
    scope: MaintenanceScopeSchema.prefault({}),
    notify_at_end: z.boolean().default(true),
    created_by_id: z.string().nullable().default(null),
    active: z.boolean().default(false),
    /** When the occurrence in force (or the next one) ends. */
    next_end: isoish.nullable().default(null),
    created_at: isoish,
    updated_at: isoish,
  })
  .loose();
export type MaintenanceWindow = z.infer<typeof MaintenanceWindowSchema>;

// --- Reports ----------------------------------------------------------------

export const ReportRangeSchema = z.object({ from: isoish, to: isoish }).loose();

/** One `{ key, label, count }` bucket; `key` is null for "unset". */
export const CountBucketSchema = z
  .object({
    key: z.string().nullable().default(null),
    label: z.string().nullable().default(null),
    count: z.number().int().default(0),
  })
  .loose();
export type CountBucket = z.infer<typeof CountBucketSchema>;

export const ReportSummarySchema = z
  .object({
    range: ReportRangeSchema,
    total: z.number().int().default(0),
    by_state: z.array(CountBucketSchema).default([]),
    by_severity: z.array(CountBucketSchema).default([]),
    by_integration: z.array(CountBucketSchema).default([]),
    /** Sparse: days with no alarms are omitted. */
    per_day: z.array(z.object({ day: z.string(), count: z.number().int() }).loose()).default([]),
  })
  .loose();
export type ReportSummary = z.infer<typeof ReportSummarySchema>;

export const DurationStatsSchema = z
  .object({
    severity: z.string().nullable().default(null),
    count: z.number().int().default(0),
    mean_seconds: z.number().nullable().default(null),
    p50_seconds: z.number().nullable().default(null),
    p90_seconds: z.number().nullable().default(null),
  })
  .loose();
export type DurationStats = z.infer<typeof DurationStatsSchema>;

export const ReportDurationSchema = z
  .object({
    range: ReportRangeSchema,
    metric: z.enum(['mtta', 'mttr']),
    overall: DurationStatsSchema.prefault({}),
    by_severity: z.array(DurationStatsSchema).default([]),
    /** Alarms in range that never reached the measured state. */
    unmeasured: z.number().int().default(0),
  })
  .loose();
export type ReportDuration = z.infer<typeof ReportDurationSchema>;

export const NOISIEST_KEYS = ['dedup_key', 'entity', 'integration'] as const;
export type NoisiestKey = (typeof NOISIEST_KEYS)[number];

export const NoisiestRowSchema = z
  .object({
    key: z.string().nullable().default(null),
    label: z.string().nullable().default(null),
    alarms: z.number().int().default(0),
    occurrences: z.number().int().default(0),
    last_seen: isoish.nullable().default(null),
  })
  .loose();
export type NoisiestRow = z.infer<typeof NoisiestRowSchema>;

export const ReportNoisiestSchema = z
  .object({
    range: ReportRangeSchema,
    by: z.enum(NOISIEST_KEYS).default('dedup_key'),
    items: z.array(NoisiestRowSchema).default([]),
  })
  .loose();
export type ReportNoisiest = z.infer<typeof ReportNoisiestSchema>;

// --- Settings, health, push -------------------------------------------------

export const SettingsSchema = z
  .object({
    reopen_window_minutes: z.number().int().default(10),
    flap_threshold_count: z.number().int().default(5),
    flap_window_minutes: z.number().int().default(30),
    retention_days_events: z.number().int().default(90),
    retention_days_alarms: z.number().int().default(365),
    smtp_from: z.string().nullable().default(null),
    smtp_from_name: z.string().nullable().default(null),
    smtp_reply_to: z.string().nullable().default(null),
    environment: z.string().nullable().default(null),
  })
  .loose();
export type Settings = z.infer<typeof SettingsSchema>;

export const HealthSchema = z
  .object({
    status: z.string(),
    version: z.string().default(''),
    db: z
      .object({ ok: z.boolean(), latency_ms: z.number().nullable().default(null) })
      .loose()
      .prefault({ ok: false, latency_ms: null }),
    redis: z
      .object({ ok: z.boolean(), latency_ms: z.number().nullable().default(null) })
      .loose()
      .prefault({ ok: false, latency_ms: null }),
  })
  .loose();
export type Health = z.infer<typeof HealthSchema>;

export const PushConfigSchema = z
  .object({ vapid_public_key: z.string().nullable().default(null) })
  .loose();

export const PushSubscriptionRecordSchema = z
  .object({
    id: z.string(),
    endpoint: z.string(),
    user_agent: z.string().nullable().default(null),
    last_used_at: isoish.nullable().default(null),
    created_at: isoish.optional(),
  })
  .loose();

export const PushSubscriptionListSchema = z
  .object({ items: z.array(PushSubscriptionRecordSchema).default([]) })
  .loose();
